/**
 * Every client-side path for the attention surface, in one place.
 *
 * The surface is where anything needing the user arrives — email, notifications,
 * briefs, reminders. Mail is a medium *inside* it, hence `/attention/mail/*`:
 * `/attention/sent` would assert that sent mail needs the user now.
 *
 * These are CLIENT ROUTES ONLY. The backend independently serves `/inbox`,
 * `/email` and `/later` API prefixes (`apiCall('/inbox/items')` in
 * features/inbox/services/inboxApi.ts, `/email/processed/*` in emailApi.ts,
 * `/api/later` in laterApi.ts). Those are unrelated and must never be renamed
 * to match these.
 */
export const ATTENTION_ROUTES = {
  /** The surface itself. Default view. */
  root: '/attention',
  /** Reading one item in place. */
  message: '/attention/message/:id',
  /** History (past dismissed inboxes). */
  history: '/attention/history',
  starred: '/attention/starred',
  /**
   * Longform you'll sit down with. Named for a place rather than a time: the
   * old `later` answered *when* in a model that otherwise names *where*, and
   * collided with snooze, the inbox's dated defer.
   */
  readingRoom: '/attention/reading-room',
  /**
   * Mail filed to the Bulletin Board — "I need to know, not act". The screener
   * has offered this box since it shipped, and until this route existed it was
   * the only one of the three naming a place that appeared nowhere else in the
   * product: Inbox is the inbox and Paper Trail is All Mail, but pressing 2
   * sent mail somewhere with no home.
   */
  bulletinBoard: '/attention/bulletin-board',
  /** Inbox mail a snooze is hiding, reached from the Inbox header's "N snoozed" line. */
  snoozed: '/attention/snoozed',
  mailAll: '/attention/mail/all',
  mailSent: '/attention/mail/sent',
  mailDrafts: '/attention/mail/drafts',
  mailLabels: '/attention/mail/labels',
  /** Rules, blocked senders and send-as, beside the mail they govern. */
  mailSettings: '/attention/mail/settings',
  mailSpam: '/attention/mail/spam',
} as const;

/** Builds the message path for a concrete id, derived so there is one path source. */
export const attentionMessagePath = (id: string): string =>
  ATTENTION_ROUTES.message.replace(':id', id);

/**
 * Paths this rename removes. No redirects exist for them — every link is
 * internal and was updated — so this list is what the guard test greps for.
 *
 * `/email/blocked-senders` is deliberately absent: it is a backend endpoint
 * (emailActionsApi.ts) and never was a client route, so listing it here would
 * demand renaming a live API path.
 */
export const LEGACY_ATTENTION_PATHS = [
  '/inbox',
  '/inbox/message',
  '/inbox/history',
  '/later',
  '/email',
  '/email/message',
  '/email/all',
  '/email/starred',
  '/email/sent',
  '/email/drafts',
  '/email/labels',
  '/email/spam',
  '/email/actions',
  // Superseded by /attention/reading-room. Listed here for the same reason as
  // the rest: no redirect exists, so the guard test is what keeps a stale link
  // from creeping back in.
  '/attention/later',
  // The Email Actions editor is deleted when Actions and rules merge into one
  // concept. Unlike the renames above this removes a page rather than moving
  // it, so the replacement is Mail settings — where the rules now live.
  '/attention/mail/actions',
] as const;
